import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Backspace,
  Call,
  CallEnd,
  CallMade,
  CallReceived,
  PhoneMissed,
  Dialpad as DialpadIcon,
  Hearing,
  Mic,
  MicOff,
  Contacts as ContactsIcon,
  History,
  VolumeUp,
  Refresh,
  DeleteOutline,
  DoNotDisturbOn,
  DoNotDisturbOff,
  Settings as SettingsIcon,
  ArrowBack,
  Videocam,
  VideocamOff,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useOrientation } from '../hooks/useOrientation';
import { useHorizontalTabSwipe } from '../hooks/useHorizontalTabSwipe';
import SubPageHeader from '../components/shared/SubPageHeader';
import SoftphoneSettingsPanel from '../components/softphone/SoftphoneSettingsPanel';
import {
  fetchSoftphoneContacts,
  fetchSoftphoneHistory,
  fetchSoftphoneMe,
  reportSoftphoneAnswered,
  reportSoftphoneEnded,
  type SoftphoneContact,
  type SoftphoneHistoryItem,
} from '../services/softphone/softphoneApi';
import { sipSoftphone, type SoftphoneLogEntry } from '../services/softphone/SipSoftphone';
import { eventBus } from '../services/EventBus';
import {
  clearDoorbellIncomingContext,
  clearSoftphoneDoorbellSession,
  consumeIncomingSoftphoneIntent,
  hasIncomingSoftphoneIntent,
  isDoorbellIncomingContext,
  isIncomingSoftphoneHandled,
  isPhoneIncomingDeepLink,
  isSoftphoneDoorbellDeepLink,
  isSoftphoneDoorbellSession,
  isSoftphoneQuickAnswer,
  markIncomingSoftphoneHandled,
  needsPhoneHandoff,
} from '../utils/sessionFlags';
import { requestClosePwaFromUserGesture } from '../utils/softphonePwaExit';
import { kickSoftphoneStart } from '../services/softphone/kickSoftphoneStart';
import DoorbellCallView from '../components/softphone/DoorbellCallView';
import PhoneVideoStage from '../components/softphone/PhoneVideoStage';
import type { SoftphoneDndEffective } from '../utils/softphoneDnd';

const ACCENT = '#2391FF';
const BG = '#1a1a1a';
const PAD_KEYS: Array<{ key: string; letters: string }> = [
  { key: '1', letters: '' },
  { key: '2', letters: 'ABC' },
  { key: '3', letters: 'DEF' },
  { key: '4', letters: 'GHI' },
  { key: '5', letters: 'JKL' },
  { key: '6', letters: 'MNO' },
  { key: '7', letters: 'PQRS' },
  { key: '8', letters: 'TUV' },
  { key: '9', letters: 'WXYZ' },
  { key: '*', letters: '' },
  { key: '0', letters: '+' },
  { key: '#', letters: '' },
];

const listPanelSx = {
  borderRadius: 2.5,
  overflow: 'hidden',
  background: 'linear-gradient(180deg, #1a2230 0%, #10141a 100%)',
  border: '1px solid rgba(35,145,255,0.28)',
  boxShadow: '0 6px 20px rgba(0,0,0,0.45)',
} as const;

const listRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  px: 1.5,
  py: 1.15,
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  transition: 'background 100ms ease, border-color 100ms ease',
  '&:last-of-type': { borderBottom: 'none' },
  '&:active': {
    background: 'rgba(35,145,255,0.12)',
  },
  '@media (hover: hover)': {
    '&:hover': {
      background: 'rgba(35,145,255,0.08)',
    },
  },
} as const;

function contactDisplayName(c: SoftphoneContact, doorbellLabel?: string): string {
  if (c.kind === 'doorbell') {
    return (doorbellLabel || c.name || '').trim() || c.extension;
  }
  const raw = (c.name || c.label || c.username || '').trim();
  return raw || c.extension;
}

function formatDurationParts(sec: number | null | undefined): { m: number; s: string } | null {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return null;
  const total = Math.round(sec);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return { m, s: String(s).padStart(2, '0') };
}

function formatShortWhen(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface SoftphonePageProps {
  onBack: () => void;
}

const SoftphonePage: React.FC<SoftphonePageProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const isHorizontal = useOrientation();
  const quickIsland = isSoftphoneQuickAnswer();
  const [doorbellMode, setDoorbellMode] = useState(
    () => isSoftphoneDoorbellSession() || isSoftphoneDoorbellDeepLink(),
  );
  const [activeTab, setActiveTab] = useState(0);
  const [digits, setDigits] = useState('');
  const [regStatus, setRegStatus] = useState(sipSoftphone.regStatus);
  const [regError, setRegError] = useState(sipSoftphone.regError);
  const [extension, setExtension] = useState(sipSoftphone.extension);
  const [logs, setLogs] = useState<SoftphoneLogEntry[]>(sipSoftphone.logs);
  const [activeCall, setActiveCall] = useState(sipSoftphone.activeCall);
  const [contacts, setContacts] = useState<SoftphoneContact[]>([]);
  const [history, setHistory] = useState<SoftphoneHistoryItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dndEffective, setDndEffective] = useState<SoftphoneDndEffective>(sipSoftphone.dndEffective);
  const [canAnswerDoorbell, setCanAnswerDoorbell] = useState(false);
  const [settingsSubView, setSettingsSubView] = useState<'main' | 'logs'>('main');
  const [pressedKey, setPressedKey] = useState<string | null>(null);
  const [callPressed, setCallPressed] = useState(false);
  const tabSwipe = useHorizontalTabSwipe({
    tabCount: 4,
    activeIndex: activeTab,
    onIndexChange: (next) => {
      setActiveTab(next);
      if (next === 3) setSettingsSubView('main');
    },
    // Video de la llamada: no cambiar de tab por debajo de la cámara.
    extraIgnoreSelector: '[data-phone-no-swipe]',
  });
  const logsBoxRef = useRef<HTMLDivElement | null>(null);
  const pressClearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const doorbellModeRef = useRef(doorbellMode);
  doorbellModeRef.current = doorbellMode;

  useEffect(() => {
    if (doorbellMode || isDoorbellIncomingContext()) return;
    void kickSoftphoneStart();
  }, [doorbellMode]);

  useEffect(() => {
    if (doorbellMode || isDoorbellIncomingContext()) return;
    let done = false;
    const tryReclaim = () => {
      if (done) return;
      if (isDoorbellIncomingContext()) return;
      if (sipSoftphone.activeCall) {
        done = true;
        return;
      }
      if (sipSoftphone.regStatus !== 'registered') return;
      if (isIncomingSoftphoneHandled()) return;
      if (!needsPhoneHandoff()) return;
      done = true;
      void sipSoftphone.notifyServerReady({ reclaim: true }).then((data) => {
        if (sipSoftphone.activeCall) return;
        const alreadySent = Boolean(
          data?.transferred
          || data?.reason === 'already_transferring'
          || data?.reason === 'reclaimed',
        );
        if (alreadySent) {
          window.setTimeout(() => {
            if (!sipSoftphone.activeCall) sipSoftphone.startHandoffRetries();
          }, 1000);
          return;
        }
        sipSoftphone.startHandoffRetries();
      });
    };
    tryReclaim();
    return sipSoftphone.subscribe(tryReclaim);
  }, [doorbellMode]);

  useEffect(() => {
    return sipSoftphone.subscribe(() => {
      setRegStatus(sipSoftphone.regStatus);
      setRegError(sipSoftphone.regError);
      setExtension(sipSoftphone.extension);
      setDndEffective({ ...sipSoftphone.dndEffective });
      setLogs([...sipSoftphone.logs]);
      setActiveCall(sipSoftphone.activeCall);
      const ac = sipSoftphone.activeCall;
      if (ac?.kind === 'doorbell' && ac.phase !== 'ended') {
        setDoorbellMode(true);
      } else if (ac?.kind === 'phone' && ac.phase !== 'ended') {
        setDoorbellMode(false);
      } else if (!isSoftphoneDoorbellSession() && !isSoftphoneDoorbellDeepLink()) {
        setDoorbellMode(false);
      }
      if (ac?.kind === 'phone' && ac.phase === 'ended') {
        consumeIncomingSoftphoneIntent();
      }
    });
  }, []);

  useEffect(() => {
    if (isPhoneIncomingDeepLink() || (hasIncomingSoftphoneIntent() && !isSoftphoneDoorbellDeepLink())) {
      clearDoorbellIncomingContext();
      setDoorbellMode(false);
    } else if (isSoftphoneDoorbellSession() || isSoftphoneDoorbellDeepLink()) {
      setDoorbellMode(true);
    }
    const unsubIn = eventBus.on('softphone:incoming', (payload: { kind?: string }) => {
      if (payload?.kind === 'doorbell') setDoorbellMode(true);
      if (payload?.kind === 'phone') {
        clearDoorbellIncomingContext();
        setDoorbellMode(false);
      }
    });
    const unsubOpen = eventBus.on('softphone:open-doorbell', () => {
      setDoorbellMode(true);
    });
    const unsubPhone = eventBus.on('softphone:open-phone', () => {
      clearDoorbellIncomingContext();
      setDoorbellMode(false);
    });
    const unsubDoorHangup = eventBus.on('softphone:doorbell-hangup', () => {
      if (isSoftphoneQuickAnswer()) return;
      clearSoftphoneDoorbellSession();
      setDoorbellMode(false);
    });
    return () => {
      unsubIn();
      unsubOpen();
      unsubPhone();
      unsubDoorHangup();
    };
  }, []);

  // Chrome móvil: desbloquear AudioContext con el primer gesto en Teléfono.
  // Pre-warm de mic solo en móvil y fuera de la isla (se suelta si no hay llamada).
  useEffect(() => {
    sipSoftphone.unlockAudio();
    const unlock = () => {
      sipSoftphone.unlockAudio();
      if (!doorbellModeRef.current) void sipSoftphone.warmupMic('idle');
    };
    window.addEventListener('pointerdown', unlock, { passive: true });
    window.addEventListener('touchstart', unlock, { passive: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
  }, []);

  useEffect(() => {
    if (doorbellMode) return;
    void fetchSoftphoneMe()
      .then((me) => {
        if (me.softphone_sip_extension) {
          setExtension((prev) => prev || me.softphone_sip_extension);
        }
        setCanAnswerDoorbell(Boolean(me.can_answer_doorbell));
        sipSoftphone.applyDndSettings({
          manualEnabled: Boolean(me.softphone_dnd),
          manualScope: me.softphone_dnd_scope || 'both',
          rules: me.softphone_dnd_rules || [],
          serverTimezone: me.server_timezone,
          effective: me.dnd_effective,
        });
        if (me.dnd_effective) setDndEffective(me.dnd_effective);
      })
      .catch(() => { /* ignore */ });
  }, [doorbellMode]);

  useEffect(() => {
    if (activeTab !== 3 || settingsSubView !== 'logs') return;
    const box = logsBoxRef.current;
    if (!box) return;
    box.scrollTop = box.scrollHeight;
  }, [logs, activeTab, settingsSubView]);

  // Refresco del indicador naranja cuando entra/sale una franja programada.
  useEffect(() => {
    const tick = () => {
      sipSoftphone.applyDndSettings({
        manualEnabled: sipSoftphone.dnd,
        manualScope: sipSoftphone.dndManualScope,
        rules: sipSoftphone.dndRules,
        serverTimezone: sipSoftphone.serverTimezone,
      });
      setDndEffective({ ...sipSoftphone.dndEffective });
    };
    const id = window.setInterval(tick, 30000);
    return () => window.clearInterval(id);
  }, []);

  const loadLists = useCallback(async () => {
    try {
      const [c, h] = await Promise.all([
        fetchSoftphoneContacts().catch(() => []),
        fetchSoftphoneHistory().catch(() => []),
      ]);
      setContacts(c);
      setHistory(h);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (doorbellMode) return;
    void loadLists();
  }, [loadLists, doorbellMode]);

  // report ringing + navegación: lo hace SipSoftphone.publishIncoming (aunque no estés en Teléfono)

  const inCall = Boolean(
    activeCall && (activeCall.phase === 'active' || activeCall.phase === 'calling' || activeCall.phase === 'ringing'),
  );
  const awaitingIncoming = !doorbellMode && !inCall && needsPhoneHandoff();
  const incomingWaitHint = regStatus === 'registered'
    ? t('softphone.call.waitingInvite')
    : regStatus === 'error'
      ? t('softphone.call.waitingError')
      : t('softphone.call.waitingRegister');

  useEffect(() => {
    if (!inCall || doorbellMode || isDoorbellIncomingContext()) return;
    markIncomingSoftphoneHandled();
    consumeIncomingSoftphoneIntent();
  }, [inCall, doorbellMode]);

  const statusChip = (() => {
    const chipSx = {
      height: 18,
      fontSize: '0.58rem',
      fontWeight: 600,
      '& .MuiChip-label': { px: 0.55, py: 0 },
    } as const;
    if (activeCall && (activeCall.phase === 'active' || activeCall.phase === 'calling' || activeCall.phase === 'ringing')) {
      return (
        <Chip
          size="small"
          label={t('softphone.status.inCall')}
          sx={{
            ...chipSx,
            backgroundColor: 'rgba(35,145,255,0.2)',
            color: ACCENT,
            boxShadow: `0 0 8px ${ACCENT}55`,
          }}
        />
      );
    }
    if (regStatus === 'registered') {
      return (
        <Chip
          size="small"
          label={t('softphone.status.registered')}
          sx={{
            ...chipSx,
            backgroundColor: 'rgba(76,175,80,0.2)',
            color: '#81c784',
          }}
        />
      );
    }
    if (regStatus === 'connecting') {
      return (
        <Chip
          size="small"
          label={t('softphone.status.connecting')}
          sx={{
            ...chipSx,
            backgroundColor: 'rgba(255,193,7,0.15)',
            color: '#ffc107',
          }}
        />
      );
    }
    if (regStatus === 'error') {
      return (
        <Chip
          size="small"
          label={t('softphone.status.error')}
          sx={{
            ...chipSx,
            backgroundColor: 'rgba(244,67,54,0.2)',
            color: '#ef9a9a',
          }}
        />
      );
    }
    return (
      <Chip
        size="small"
        label={t('softphone.status.unregistered')}
        sx={{
          ...chipSx,
          backgroundColor: 'rgba(244,67,54,0.15)',
          color: '#ef9a9a',
        }}
      />
    );
  })();

  const handleDigit = (d: string) => {
    if (activeCall?.phase === 'active') {
      void sipSoftphone.sendDtmf(d);
    }
    setDigits((prev) => (prev.length >= 32 ? prev : prev + d));
  };

  const flashKey = (k: string) => {
    if (pressClearTimer.current) clearTimeout(pressClearTimer.current);
    setPressedKey(k);
    pressClearTimer.current = setTimeout(() => {
      setPressedKey(null);
      pressClearTimer.current = null;
    }, 120);
  };

  const onPadPointerDown = (k: string) => {
    if (pressClearTimer.current) clearTimeout(pressClearTimer.current);
    setPressedKey(k);
  };

  const onPadPointerUp = (k: string) => {
    flashKey(k);
    handleDigit(k);
  };

  const onPadPointerCancel = () => {
    setPressedKey(null);
  };

  const handleCall = async (target?: string) => {
    const num = (target ?? digits).trim();
    if (!num) return;
    const toDoorbell = sipSoftphone.isDoorbellDest(num);
    if (toDoorbell) setDoorbellMode(true);
    setBusy(true);
    setError(null);
    try {
      await sipSoftphone.call(num);
      setDigits('');
    } catch (e: any) {
      setError(e?.message || t('softphone.errors.callFailed'));
      if (toDoorbell && !isSoftphoneQuickAnswer() && !isSoftphoneDoorbellSession()) {
        setDoorbellMode(false);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleAnswer = async () => {
    setBusy(true);
    try {
      await sipSoftphone.accept();
      if (activeCall?.id) {
        void reportSoftphoneAnswered(activeCall.id).catch(() => { /* ignore */ });
      }
    } catch (e: any) {
      setError(e?.message || t('softphone.errors.answerFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleHangup = async () => {
    markIncomingSoftphoneHandled();
    consumeIncomingSoftphoneIntent();
    if (isSoftphoneQuickAnswer()) {
      requestClosePwaFromUserGesture();
    }
    const snap = await sipSoftphone.hangup();
    if (snap?.id) {
      void reportSoftphoneEnded(snap.id, {
        state: snap.phase === 'ringing' ? 'missed' : 'ended',
        peer: snap.peer,
        direction: snap.direction,
        kind: snap.kind,
        duration_sec: Math.max(0, Math.round((Date.now() - snap.startedAt) / 1000)),
      }).catch(() => { /* ignore */ });
    }
    if (isSoftphoneQuickAnswer()) {
      try {
        eventBus.emit('softphone:call-ended');
      } catch { /* ignore */ }
      return;
    }
    try {
      eventBus.emit('softphone:house-ensure');
    } catch { /* ignore */ }
    void loadLists();
  };

  const handleReregister = async () => {
    setBusy(true);
    setError(null);
    try {
      await sipSoftphone.reregister();
    } finally {
      setBusy(false);
    }
  };

  const videoUiOn = Boolean(
    !doorbellMode
    && inCall
    && activeCall?.kind === 'phone'
    && activeCall.videoUiOn,
  );
  const callPeerLabel = contactDisplayName(
    contacts.find((c) => c.extension === activeCall?.peer) || {
      extension: activeCall?.peer || '',
      name: activeCall?.peer || '',
    },
    t('softphone.contacts.doorbell'),
  );
  const callStatusLabel = activeCall?.phase === 'ringing'
    ? t('softphone.call.ringing')
    : activeCall?.phase === 'calling'
      ? t('softphone.call.calling')
      : t('softphone.call.active');

  const videoToggleButton = (compact: boolean) => {
    if (!activeCall?.wantsVideo || activeCall.phase !== 'active') return null;
    const on = Boolean(activeCall.videoUiOn);
    const size = compact ? 42 : 44;
    return (
      <IconButton
        onClick={() => void sipSoftphone.setVideoUi(!on)}
        aria-label={t(on ? 'softphone.video.cameraOff' : 'softphone.video.cameraOn') as string}
        sx={{
          width: size,
          height: size,
          background: on
            ? `linear-gradient(145deg, ${ACCENT}, #1565c0)`
            : (compact ? BG : 'linear-gradient(145deg, #2a2a2a, #1a1a1a)'),
          color: '#fff',
          border: '1px solid #555',
          boxShadow: on ? `0 0 12px ${ACCENT}66` : 'none',
        }}
      >
        {on ? <Videocam sx={{ fontSize: 20 }} /> : <VideocamOff sx={{ fontSize: 20 }} />}
      </IconButton>
    );
  };

  const logColor = (level: SoftphoneLogEntry['level']) => {
    if (level === 'ok') return '#81c784';
    if (level === 'error') return '#ef5350';
    if (level === 'warn') return '#ffb74d';
    return '#b0b0b0';
  };

  if (doorbellMode) {
    return (
      <Box
        sx={{
          height: '100%',
          maxHeight: '100%',
          position: 'relative',
          backgroundColor: '#000',
          overflow: 'hidden',
        }}
      >
        <DoorbellCallView />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'radial-gradient(ellipse at 50% 0%, #0d1a28 0%, #000 55%)',
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative',
        boxSizing: 'border-box',
        paddingBottom: quickIsland ? 0 : (isHorizontal ? '58px' : '56px'),
      }}
      onTouchStart={tabSwipe.onTouchStart}
      onTouchMove={tabSwipe.onTouchMove}
      onTouchEnd={tabSwipe.onTouchEnd}
    >
      <SubPageHeader
        title={t('softphone.title')}
        onBack={quickIsland ? undefined : onBack}
        isHorizontal={isHorizontal}
        rightContent={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            {extension ? (
              <Typography
                sx={{
                  color: '#9ec9ff',
                  fontSize: '0.68rem',
                  fontFamily: 'monospace',
                  whiteSpace: 'nowrap',
                }}
              >
                {extension}
              </Typography>
            ) : null}
            <IconButton
              size="small"
              disableRipple
              aria-label={
                dndEffective.active
                  ? (t(dndEffective.source === 'manual' ? 'softphone.dnd.onManual' : 'softphone.dnd.onSchedule') as string)
                  : (t('softphone.dnd.off') as string)
              }
              title={
                dndEffective.active
                  ? (t(dndEffective.source === 'manual' ? 'softphone.dnd.onManualHint' : 'softphone.dnd.onScheduleHint') as string)
                  : (t('softphone.dnd.offHint') as string)
              }
              onClick={() => {
                setActiveTab(3);
                setSettingsSubView('main');
              }}
              sx={{
                color: dndEffective.active
                  ? (dndEffective.color === 'orange' ? '#ffb74d' : '#ef5350')
                  : '#666',
                p: 0.35,
                cursor: 'pointer',
                border: dndEffective.active
                  ? `1px solid ${dndEffective.color === 'orange' ? '#ffb74d88' : '#ef535088'}`
                  : '1px solid transparent',
                backgroundColor: dndEffective.active
                  ? (dndEffective.color === 'orange' ? 'rgba(255,183,77,0.12)' : 'rgba(239,83,80,0.12)')
                  : 'transparent',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
              }}
            >
              {dndEffective.active
                ? <DoNotDisturbOn sx={{ fontSize: 17 }} />
                : <DoNotDisturbOff sx={{ fontSize: 17 }} />}
            </IconButton>
            {statusChip}
          </Box>
        )}
        sx={{
          background: `linear-gradient(180deg, ${BG} 0%, #0a0a0a 100%)`,
          borderBottom: '1px solid rgba(35,145,255,0.25)',
          position: 'relative', // no sticky: overflow:hidden del padre lo rompe y “pierde” la barra
          top: 'auto',
          flexShrink: 0,
        }}
        backButtonSx={{ color: ACCENT }}
      />

      {(regError || (!extension && !isHorizontal)) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 0.4,
            borderBottom: '1px solid #222',
            flexShrink: 0,
          }}
        >
          {!extension && (
            <Typography sx={{ color: '#888', fontSize: '0.72rem' }}>
              {t('softphone.myExtensionNone')}
            </Typography>
          )}
          {regError && (
            <Typography
              sx={{
                color: '#ef9a9a',
                fontSize: '0.68rem',
                ml: 'auto',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={regError}
            >
              {regError}
            </Typography>
          )}
        </Box>
      )}

      {videoUiOn && activeCall ? (
        <PhoneVideoStage
          peerLabel={callPeerLabel}
          statusLabel={callStatusLabel}
          cameraOn={Boolean(activeCall.cameraOn)}
          facingUser={activeCall.facingUser !== false}
          hasRemoteVideo={Boolean(activeCall.hasRemoteVideo)}
          muted={Boolean(activeCall.muted)}
          speaker={Boolean(activeCall.speaker)}
          onToggleMute={() => void sipSoftphone.setMute(!activeCall.muted)}
          onToggleSpeaker={() => void sipSoftphone.setSpeaker(!activeCall.speaker)}
          onToggleCamera={() => {
            if (activeCall.cameraOn) {
              void sipSoftphone.setVideoUi(false);
            } else {
              void sipSoftphone.setCameraEnabled(true);
            }
          }}
          onFlipCamera={() => void sipSoftphone.flipCamera()}
          onHangup={() => void handleHangup()}
        />
      ) : null}

      {!videoUiOn && (
      <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: isHorizontal ? 1.25 : 0.75,
          borderBottom: `1px solid ${ACCENT}`,
          minHeight: isHorizontal ? 40 : 36,
          flexShrink: 0,
          px: 0.5,
          overflowX: 'auto',
          backgroundColor: '#000',
        }}
      >
        {[
          { id: 0, label: t('softphone.tabs.dialpad'), icon: <DialpadIcon sx={{ fontSize: 16 }} /> },
          { id: 1, label: t('softphone.tabs.contacts'), icon: <ContactsIcon sx={{ fontSize: 16 }} /> },
          { id: 2, label: t('softphone.tabs.recent'), icon: <History sx={{ fontSize: 16 }} /> },
          { id: 3, label: t('softphone.tabs.settings'), icon: <SettingsIcon sx={{ fontSize: 16 }} /> },
        ].map((tab) => (
          <Button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              if (tab.id === 3) setSettingsSubView('main');
            }}
            sx={{
              color: activeTab === tab.id ? ACCENT : '#fff',
              fontWeight: activeTab === tab.id ? 'bold' : 'normal',
              fontSize: isHorizontal ? 13 : 11.5,
              textTransform: 'none',
              minWidth: 'auto',
              px: isHorizontal ? 2 : 1.1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.4,
              whiteSpace: 'nowrap',
            }}
          >
            {tab.icon}
            {tab.label}
          </Button>
        ))}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          // Teclado: un poco menos de padding lateral para aprovechar el ancho en vertical.
          px: activeTab === 0 ? 1.25 : 2,
          py: 1.5,
          display: 'flex',
          flexDirection: 'column',
          // En logs: sin scroll del contenedor padre (scroll solo en el panel de logs).
          overflow: activeTab === 3 && settingsSubView === 'logs' ? 'hidden' : 'auto',
        }}
      >
        {error && (
          <Typography sx={{ color: '#ef5350', fontSize: '0.8rem', mb: 1 }}>{error}</Typography>
        )}

        {/* En otras tabs: si hay llamada, mostrar controles arriba */}
        {activeTab !== 0 && (inCall || awaitingIncoming) && (
          <Box
            sx={{
              backgroundColor: BG,
              border: `1px solid ${ACCENT}55`,
              borderRadius: 2,
              p: 1.5,
              mb: 1.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              boxShadow: `0 0 24px ${ACCENT}22`,
            }}
          >
            <Typography sx={{ color: '#fff', fontWeight: 600 }}>
              {awaitingIncoming ? t('softphone.call.ringing') : (activeCall?.peer || '—')}
            </Typography>
            <Typography sx={{ color: '#999', fontSize: '0.75rem', textAlign: 'center', px: 1 }}>
              {awaitingIncoming
                ? incomingWaitHint
                : activeCall?.phase === 'ringing'
                  ? t('softphone.call.ringing')
                  : activeCall?.phase === 'calling'
                    ? t('softphone.call.calling')
                    : t('softphone.call.active')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.25, mt: 0.5, alignItems: 'center' }}>
              {activeCall?.phase === 'ringing' && activeCall.direction === 'in' && (
                <IconButton
                  onClick={() => void handleAnswer()}
                  disabled={busy}
                  sx={{ width: 42, height: 42, backgroundColor: '#2e7d32', color: '#fff' }}
                >
                  <Call sx={{ fontSize: 20 }} />
                </IconButton>
              )}
              {activeCall?.phase === 'active' && (
                <>
                  <IconButton
                    onClick={() => void sipSoftphone.setMute(!activeCall.muted)}
                    sx={{
                      width: 42,
                      height: 42,
                      backgroundColor: activeCall.muted ? '#c62828' : BG,
                      color: '#fff',
                      border: '1px solid #444',
                    }}
                  >
                    {activeCall.muted ? <MicOff sx={{ fontSize: 20 }} /> : <Mic sx={{ fontSize: 20 }} />}
                  </IconButton>
                  <IconButton
                    onClick={() => void sipSoftphone.setSpeaker(!activeCall.speaker)}
                    sx={{
                      width: 42,
                      height: 42,
                      backgroundColor: activeCall.speaker ? ACCENT : BG,
                      color: '#fff',
                      border: '1px solid #444',
                    }}
                  >
                    {activeCall.speaker ? <VolumeUp sx={{ fontSize: 20 }} /> : <Hearing sx={{ fontSize: 20 }} />}
                  </IconButton>
                  {videoToggleButton(true)}
                </>
              )}
              {inCall && (
                <IconButton
                  onClick={() => void handleHangup()}
                  sx={{ width: 42, height: 42, backgroundColor: '#c62828', color: '#fff' }}
                >
                  <CallEnd sx={{ fontSize: 20 }} />
                </IconButton>
              )}
              {awaitingIncoming && (
                <CircularProgress size={18} sx={{ color: ACCENT, mt: 0.25 }} />
              )}
            </Box>
          </Box>
        )}

        {activeTab === 0 && (
          <Box
            sx={{
              width: '100%',
              // Vertical: casi todo el ancho. Horizontal: tope para que las teclas no se estiren.
              maxWidth: isHorizontal ? 380 : 440,
              mx: 'auto',
            }}
          >
            {/* Display */}
            <Box
              sx={{
                background: (inCall || awaitingIncoming)
                  ? 'linear-gradient(180deg, #1a1a1a 0%, #0a0a0a 100%)'
                  : 'linear-gradient(180deg, #141414 0%, #0a0a0a 100%)',
                border: `1px solid ${(inCall || awaitingIncoming) ? 'rgba(35,145,255,0.55)' : '#3a3a3a'}`,
                borderRadius: 2.5,
                p: isHorizontal ? 1.5 : 2,
                mb: 2,
                height: isHorizontal ? 128 : 156,
                minHeight: isHorizontal ? 128 : 156,
                maxHeight: isHorizontal ? 128 : 156,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                boxShadow: '0 6px 20px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04)',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {awaitingIncoming ? (
                <>
                  <CircularProgress size={22} sx={{ color: ACCENT, mb: 0.25 }} />
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.2rem', letterSpacing: 1.2 }}>
                    {t('softphone.call.ringing')}
                  </Typography>
                  <Typography sx={{ color: '#9ec9ff', fontSize: '0.78rem', fontWeight: 500, textAlign: 'center', px: 1.5 }}>
                    {incomingWaitHint}
                  </Typography>
                </>
              ) : inCall ? (
                <>
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.2rem', letterSpacing: 1.2 }}>
                    {activeCall?.peer || '—'}
                  </Typography>
                  <Typography sx={{ color: '#9ec9ff', fontSize: '0.78rem', fontWeight: 500 }}>
                    {activeCall?.phase === 'ringing'
                      ? t('softphone.call.ringing')
                      : activeCall?.phase === 'calling'
                        ? t('softphone.call.calling')
                        : t('softphone.call.active')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.25, mt: 0.5, alignItems: 'center' }}>
                    {activeCall?.phase === 'ringing' && activeCall.direction === 'in' && (
                      <IconButton
                        onClick={() => void handleAnswer()}
                        disabled={busy}
                        sx={{
                          width: 46,
                          height: 46,
                          background: 'linear-gradient(145deg, #43a047, #2e7d32)',
                          color: '#fff',
                          boxShadow: '0 0 16px rgba(76,175,80,0.45)',
                        }}
                      >
                        <Call sx={{ fontSize: 22 }} />
                      </IconButton>
                    )}
                    {activeCall?.phase === 'active' && (
                      <>
                        <IconButton
                          onClick={() => void sipSoftphone.setMute(!activeCall.muted)}
                          sx={{
                            width: 44,
                            height: 44,
                            background: activeCall.muted
                              ? 'linear-gradient(145deg, #e53935, #c62828)'
                              : 'linear-gradient(145deg, #2a2a2a, #1a1a1a)',
                            color: '#fff',
                            border: '1px solid #555',
                            boxShadow: activeCall.muted ? '0 0 12px rgba(239,83,80,0.4)' : 'none',
                          }}
                        >
                          {activeCall.muted ? <MicOff sx={{ fontSize: 20 }} /> : <Mic sx={{ fontSize: 20 }} />}
                        </IconButton>
                        <IconButton
                          onClick={() => void sipSoftphone.setSpeaker(!activeCall.speaker)}
                          sx={{
                            width: 44,
                            height: 44,
                            background: activeCall.speaker
                              ? `linear-gradient(145deg, ${ACCENT}, #1565c0)`
                              : 'linear-gradient(145deg, #2a2a2a, #1a1a1a)',
                            color: '#fff',
                            border: '1px solid #555',
                            boxShadow: activeCall.speaker ? `0 0 12px ${ACCENT}66` : 'none',
                          }}
                          aria-label={t('softphone.controls.speaker') as string}
                        >
                          {activeCall.speaker ? <VolumeUp sx={{ fontSize: 20 }} /> : <Hearing sx={{ fontSize: 20 }} />}
                        </IconButton>
                        {videoToggleButton(false)}
                      </>
                    )}
                    <IconButton
                      onClick={() => void handleHangup()}
                      sx={{
                        width: 46,
                        height: 46,
                        background: 'linear-gradient(145deg, #e53935, #b71c1c)',
                        color: '#fff',
                        boxShadow: '0 0 16px rgba(229,57,53,0.45)',
                      }}
                    >
                      <CallEnd sx={{ fontSize: 22 }} />
                    </IconButton>
                  </Box>
                </>
              ) : (
                <Typography
                  sx={{
                    color: '#fff',
                    fontSize: digits.length > 12
                      ? (isHorizontal ? '1.2rem' : '1.45rem')
                      : (isHorizontal ? '1.65rem' : '2rem'),
                    textAlign: 'center',
                    letterSpacing: 3.5,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontWeight: 600,
                    minHeight: 44,
                    width: '100%',
                    wordBreak: 'break-all',
                  }}
                >
                  {digits || '\u00A0'}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: isHorizontal ? 1 : 1.35,
                mb: 2,
                px: 0,
              }}
            >
              {PAD_KEYS.map(({ key: k, letters }) => {
                const pressed = pressedKey === k;
                const isStar = k === '*';
                const isHash = k === '#';
                return (
                  <Button
                    key={k}
                    disableRipple
                    disableFocusRipple
                    tabIndex={-1}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      onPadPointerDown(k);
                    }}
                    onPointerUp={(e) => {
                      onPadPointerUp(k);
                      (e.currentTarget as HTMLButtonElement).blur();
                    }}
                    onPointerCancel={onPadPointerCancel}
                    onPointerLeave={() => {
                      if (pressedKey === k) setPressedKey(null);
                    }}
                    sx={{
                      minHeight: isHorizontal ? 52 : 66,
                      width: '100%',
                      borderRadius: 2.5,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.15,
                      color: '#fff',
                      textTransform: 'none',
                      background: pressed
                        ? `linear-gradient(145deg, rgba(35,145,255,0.55), rgba(35,145,255,0.25))`
                        : 'linear-gradient(160deg, #2a3140 0%, #161a22 55%, #0e1014 100%)',
                      border: `1px solid ${pressed ? ACCENT : 'rgba(35,145,255,0.28)'}`,
                      boxShadow: pressed
                        ? `0 0 18px ${ACCENT}66, inset 0 1px 0 rgba(255,255,255,0.2)`
                        : '0 4px 14px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)',
                      transition: 'background 80ms ease, border-color 80ms ease, box-shadow 80ms ease, transform 80ms ease',
                      transform: pressed ? 'scale(0.97)' : 'scale(1)',
                      WebkitTapHighlightColor: 'transparent',
                      '@media (hover: hover)': {
                        '&:hover': {
                          borderColor: ACCENT,
                          boxShadow: `0 0 16px ${ACCENT}44, inset 0 1px 0 rgba(255,255,255,0.1)`,
                        },
                      },
                      '&:focus, &:focus-visible, &.Mui-focusVisible': {
                        outline: 'none',
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: isStar || isHash
                          ? (isHorizontal ? '1.45rem' : '1.75rem')
                          : (isHorizontal ? '1.4rem' : '1.7rem'),
                        fontWeight: 600,
                        lineHeight: 1,
                        transform: isStar ? 'scale(1.35) translateY(1px)' : 'none',
                        fontFamily: isStar || isHash
                          ? 'system-ui, -apple-system, sans-serif'
                          : 'inherit',
                      }}
                    >
                      {k}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: isHorizontal ? '0.52rem' : '0.62rem',
                        letterSpacing: letters.length > 3 ? 0.6 : 1.4,
                        color: 'rgba(158,201,255,0.75)',
                        fontWeight: 600,
                        lineHeight: 1,
                        minHeight: 10,
                        mt: 0.2,
                      }}
                    >
                      {letters || '\u00A0'}
                    </Box>
                  </Button>
                );
              })}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2.5, alignItems: 'center', pb: 1 }}>
              <IconButton
                onClick={() => setDigits((d) => d.slice(0, -1))}
                sx={{
                  color: '#9ec9ff',
                  width: isHorizontal ? 52 : 60,
                  height: isHorizontal ? 52 : 60,
                  borderRadius: 2.5,
                  background: 'linear-gradient(145deg, #252a35, #151820)',
                  border: '1px solid rgba(35,145,255,0.3)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                  '&:hover': { borderColor: ACCENT, background: '#1e2530' },
                }}
                aria-label={t('softphone.dialpad.backspace') as string}
              >
                <Backspace sx={{ fontSize: isHorizontal ? 24 : 28 }} />
              </IconButton>
              <IconButton
                onClick={() => void handleCall()}
                disabled={busy || !digits.trim() || regStatus !== 'registered' || inCall}
                onPointerDown={() => {
                  if (!(busy || !digits.trim() || regStatus !== 'registered' || inCall)) {
                    setCallPressed(true);
                  }
                }}
                onPointerUp={() => setCallPressed(false)}
                onPointerLeave={() => setCallPressed(false)}
                onPointerCancel={() => setCallPressed(false)}
                sx={{
                  width: isHorizontal ? 68 : 78,
                  height: isHorizontal ? 68 : 78,
                  borderRadius: 2.5,
                  background: callPressed
                    ? 'linear-gradient(145deg, #a5d6a7, #43a047)'
                    : 'linear-gradient(145deg, #66bb6a, #2e7d32)',
                  color: '#fff',
                  border: `1px solid ${callPressed ? '#c8e6c9' : 'rgba(129,199,132,0.55)'}`,
                  boxShadow: callPressed
                    ? '0 0 28px rgba(76,175,80,0.75), inset 0 0 12px rgba(255,255,255,0.25)'
                    : '0 0 22px rgba(76,175,80,0.4), 0 6px 16px rgba(0,0,0,0.45)',
                  transform: callPressed ? 'scale(0.94)' : 'scale(1)',
                  transition: 'transform 80ms ease, box-shadow 80ms ease, background 80ms ease',
                  '&:hover': {
                    background: 'linear-gradient(145deg, #81c784, #388e3c)',
                    boxShadow: '0 0 26px rgba(76,175,80,0.55)',
                  },
                  '&.Mui-disabled': {
                    background: 'linear-gradient(145deg, #4a7c4c, #2d5a2f)',
                    color: 'rgba(255,255,255,0.55)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
                    border: '1px solid rgba(76,175,80,0.35)',
                    opacity: 0.85,
                  },
                }}
              >
                <Call sx={{ fontSize: isHorizontal ? 30 : 34 }} />
              </IconButton>
              <Box sx={{ width: isHorizontal ? 52 : 60 }} />
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={listPanelSx}>
            {contacts.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  py: 4,
                  px: 2.5,
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(145deg, rgba(35,145,255,0.25), rgba(35,145,255,0.08))',
                    border: '1px solid rgba(35,145,255,0.35)',
                    mb: 0.5,
                  }}
                >
                  <ContactsIcon sx={{ color: ACCENT, fontSize: 28 }} />
                </Box>
                <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.95rem' }}>
                  {t('softphone.contacts.emptyTitle')}
                </Typography>
                <Typography sx={{ color: '#888', fontSize: '0.78rem', lineHeight: 1.45, maxWidth: 280 }}>
                  {t('softphone.contacts.emptyHint')}
                </Typography>
              </Box>
            ) : (
              contacts.map((c) => {
                const doorbellLabel = t('softphone.contacts.doorbell');
                const name = contactDisplayName(c, doorbellLabel);
                return (
                  <Box
                    key={c.extension}
                    role="button"
                    tabIndex={0}
                    onClick={() => void handleCall(c.extension)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        void handleCall(c.extension);
                      }
                    }}
                    sx={listRowSx}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.92rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {name}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#9ec9ff',
                          fontSize: '0.75rem',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          mt: 0.15,
                        }}
                      >
                        {c.extension}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      aria-label={t('softphone.contacts.call') as string}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleCall(c.extension);
                      }}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        background: 'linear-gradient(145deg, #66bb6a, #2e7d32)',
                        color: '#fff',
                        border: '1px solid rgba(129,199,132,0.45)',
                        boxShadow: '0 0 14px rgba(76,175,80,0.35)',
                        '&:hover': {
                          background: 'linear-gradient(145deg, #81c784, #388e3c)',
                        },
                      }}
                    >
                      <Call sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                );
              })
            )}
          </Box>
        )}

        {activeTab === 2 && (
          <Box sx={listPanelSx}>
            {history.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  py: 4,
                  px: 2.5,
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(145deg, rgba(35,145,255,0.25), rgba(35,145,255,0.08))',
                    border: '1px solid rgba(35,145,255,0.35)',
                    mb: 0.5,
                  }}
                >
                  <History sx={{ color: ACCENT, fontSize: 28 }} />
                </Box>
                <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.95rem' }}>
                  {t('softphone.recent.emptyTitle')}
                </Typography>
                <Typography sx={{ color: '#888', fontSize: '0.78rem', lineHeight: 1.45, maxWidth: 280 }}>
                  {t('softphone.recent.emptyHint')}
                </Typography>
              </Box>
            ) : (
              history.map((h) => {
                const missed = h.state === 'missed' || h.state === 'rejected';
                const dirIn = h.direction === 'in';
                const dirColor = missed ? '#ef5350' : dirIn ? '#66bb6a' : ACCENT;
                const DirIcon = missed ? PhoneMissed : dirIn ? CallReceived : CallMade;
                const dirLabel = t(
                  dirIn ? 'softphone.recent.direction.in' : 'softphone.recent.direction.out',
                );
                const stateKey = ['ended', 'missed', 'rejected', 'ringing'].includes(h.state)
                  ? h.state
                  : 'ended';
                const stateLabel = t(`softphone.recent.state.${stateKey}`);
                const when = formatShortWhen(h.started_at);
                const durParts = formatDurationParts(h.duration_sec);
                const metaParts = [dirLabel, stateLabel];
                if (when) metaParts.push(when);
                if (durParts) {
                  metaParts.push(t('softphone.recent.duration', durParts) as string);
                }
                const canCall = Boolean(h.peer);

                return (
                  <Box
                    key={h.id}
                    role={canCall ? 'button' : undefined}
                    tabIndex={canCall ? 0 : undefined}
                    onClick={() => {
                      if (h.peer) void handleCall(h.peer);
                    }}
                    onKeyDown={(e) => {
                      if (!h.peer) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        void handleCall(h.peer);
                      }
                    }}
                    sx={{
                      ...listRowSx,
                      cursor: canCall ? 'pointer' : 'default',
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `${dirColor}22`,
                        border: `1px solid ${dirColor}66`,
                        color: dirColor,
                      }}
                    >
                      <DirIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.92rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {h.peer || '—'}
                        </Typography>
                        {h.kind === 'doorbell' && (
                          <Chip
                            size="small"
                            label={t('softphone.recent.kind.doorbell')}
                            sx={{
                              height: 20,
                              fontSize: '0.62rem',
                              fontWeight: 600,
                              color: '#ffb74d',
                              backgroundColor: 'rgba(255,183,77,0.15)',
                              border: '1px solid rgba(255,183,77,0.4)',
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        sx={{
                          color: '#888',
                          fontSize: '0.72rem',
                          mt: 0.2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {metaParts.join(' · ')}
                      </Typography>
                    </Box>
                    {canCall && (
                      <IconButton
                        size="small"
                        aria-label={t('softphone.recent.call') as string}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (h.peer) void handleCall(h.peer);
                        }}
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          background: 'linear-gradient(145deg, #66bb6a, #2e7d32)',
                          color: '#fff',
                          border: '1px solid rgba(129,199,132,0.45)',
                          boxShadow: '0 0 14px rgba(76,175,80,0.35)',
                          '&:hover': {
                            background: 'linear-gradient(145deg, #81c784, #388e3c)',
                          },
                        }}
                      >
                        <Call sx={{ fontSize: 20 }} />
                      </IconButton>
                    )}
                  </Box>
                );
              })
            )}
          </Box>
        )}

        {activeTab === 3 && settingsSubView === 'main' && (
          <SoftphoneSettingsPanel
            canAnswerDoorbell={canAnswerDoorbell}
            onOpenLogs={() => setSettingsSubView('logs')}
          />
        )}

        {activeTab === 3 && settingsSubView === 'logs' && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
            }}
          >
            <Button
              size="small"
              startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
              onClick={() => setSettingsSubView('main')}
              sx={{ textTransform: 'none', color: '#9ec9ff', mb: 1, px: 0, alignSelf: 'flex-start', flexShrink: 0 }}
            >
              {t('softphone.settings.backToSettings')}
            </Button>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.25, flexShrink: 0 }}>
              <Button
                size="small"
                startIcon={<Refresh sx={{ fontSize: 16 }} />}
                onClick={() => void handleReregister()}
                disabled={busy}
                sx={{
                  textTransform: 'none',
                  color: '#fff',
                  backgroundColor: ACCENT,
                  '&:hover': { backgroundColor: '#1a7de0' },
                  fontSize: '0.78rem',
                }}
              >
                {t('softphone.logs.retry')}
              </Button>
              <Button
                size="small"
                startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
                onClick={() => sipSoftphone.clearLogs()}
                sx={{
                  textTransform: 'none',
                  color: '#aaa',
                  border: '1px solid #444',
                  fontSize: '0.78rem',
                }}
              >
                {t('softphone.logs.clear')}
              </Button>
            </Box>
            <Typography sx={{ color: '#777', fontSize: '0.72rem', mb: 1, flexShrink: 0 }}>
              {t('softphone.logs.hint')}
            </Typography>
            {/* Panel de logs: ocupa el resto hasta la barra inferior; scroll interno. */}
            <Box
              ref={logsBoxRef}
              sx={{
                flex: 1,
                minHeight: 0,
                backgroundColor: '#0a0a0a',
                border: '1px solid #2a2a2a',
                borderRadius: 1,
                p: 1.25,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '0.72rem',
                overflow: 'auto',
              }}
            >
              {logs.length === 0 ? (
                <Typography sx={{ color: '#666', fontSize: '0.78rem' }}>
                  {t('softphone.logs.empty')}
                </Typography>
              ) : (
                logs.map((entry) => (
                  <Box key={entry.id} sx={{ mb: 0.65, lineHeight: 1.35 }}>
                    <Box component="span" sx={{ color: '#555', mr: 0.75 }}>
                      {new Date(entry.at).toLocaleTimeString()}
                    </Box>
                    <Box component="span" sx={{ color: logColor(entry.level), fontWeight: 600, mr: 0.75 }}>
                      {entry.level.toUpperCase()}
                    </Box>
                    <Box component="span" sx={{ color: '#ddd' }}>{entry.message}</Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        )}
      </Box>
      </>
      )}
    </Box>
  );
};

export default SoftphonePage;
